/**
 * @file DesktopDropdownMenuView — presentational menu chrome.
 * @description Renders the shared desktop dropdown-menu surface on top of the
 * `FixedPositionDropdownShell` atom (SE-5570 — replaces the retired gluestack
 * Menu atom), which supplies trigger-anchored measured positioning, viewport
 * clamping with flip-above, the native Modal host that stacks above elevated
 * chrome, and backdrop/hardware-back dismissal. Item rows render at a fixed
 * 45px height with 12px horizontal padding, optional leading icon,
 * destructive/disabled styling, and an optional right-aligned
 * keyboard-shortcut hint. Width and row height are runtime/Figma-fixed pixel
 * values applied via dynamic-style waivers since they are not on the closed
 * token scale. Each item's `onPress` is supplied already-decorated by the
 * container (fires the caller handler then closes), so this view passes the
 * stable reference straight through — no inline arrow.
 * @module components/molecules/DesktopDropdownMenu/DesktopDropdownMenuView
 */
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import {
  FixedPositionDropdownShell,
  FixedPositionDropdownShellBackdrop,
  FixedPositionDropdownShellPanel,
  FixedPositionDropdownShellTrigger,
  type FixedPositionDropdownShellTriggerRef,
  type DropdownMeasurableNode,
  type DropdownPanelGeometry,
} from "@/components/atoms/FixedPositionDropdownShell";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_DESKTOP_DROPDOWN_MENU } from "e2e/testId";

/**
 * Placement of the menu surface relative to its anchor. With the shell, the
 * horizontal half ("-start" = left edge, "-end" = right edge) drives the
 * anchor; the vertical half is advisory — the shell opens below and flips
 * above automatically when there is no room (SE-5568 geometry).
 */
export type DesktopDropdownMenuPlacement =
  | "bottom-start"
  | "bottom-end"
  | "top-start"
  | "top-end";

/** A single selectable row in the menu. */
export interface DesktopDropdownMenuItem {
  /** Stable identity — also used to derive the row testID. */
  id: string;
  /** Visible label. */
  label: string;
  /** Optional leading lucide icon. */
  icon?: LucideIcon;
  /** Invoked when the row is activated (the menu also auto-closes). */
  onPress: () => void;
  /** When true the row is non-interactive and renders muted. */
  disabled?: boolean;
  /** Separates action groups in the same menu. */
  dividerBefore?: boolean;
  /** When true the row renders in the destructive (error) color. */
  destructive?: boolean;
  /** Optional right-aligned keyboard-shortcut hint (e.g. "⌘⌫"). */
  keyboardShortcut?: string;
  /**
   * Optional row testID override — preserves consumer e2e contracts.
   * Defaults to `TEST_ID_DESKTOP_DROPDOWN_MENU.itemFor(id)`.
   */
  testID?: string;
}

/** Default menu width in px (Figma "Menu/Player-create" chrome). */
export const DESKTOP_DROPDOWN_MENU_DEFAULT_WIDTH = 210;

/**
 * Panel max height in px — enables the shell's bottom-edge clamp/flip and
 * caps very long menus behind the internal ScrollView.
 */
export const DESKTOP_DROPDOWN_MENU_MAX_HEIGHT = 400;

/**
 * Row height class. The Figma chrome specifies 45px; the closed sizing scale
 * has no 45, so rows snap to `h-11` (44px — ties round down per the snap
 * tables). The atom Pressable accepts no style prop, so the off-scale px
 * cannot be carried as a dynamic style either.
 */
const ROW_CLASS = "h-11";

/**
 * Maps each placement to the correct caret icon so the pointer always
 * points toward the anchor regardless of menu direction.
 */
const POINTER_ICON_BY_PLACEMENT: Record<
  DesktopDropdownMenuPlacement,
  typeof ChevronUpIcon
> = {
  "bottom-start": ChevronUpIcon,
  "bottom-end": ChevronUpIcon,
  "top-start": ChevronDownIcon,
  "top-end": ChevronDownIcon,
};

/**
 * Resolves the label/icon color class for a row from its state.
 * @param item - The menu item whose state drives the color.
 * @returns A semantic content-token class string.
 */
const colorClassFor = (item: DesktopDropdownMenuItem): string => {
  if (item.disabled) return "text-content-disabled";
  if (item.destructive) return "text-status-error";
  return "text-content-primary";
};

/**
 * Renders a single menu row. A plain render helper (not a component) so it does
 * not trip the one-component-per-file rule yet keeps the JSX map readable.
 * @param item - The item to render. Its `onPress` is already decorated by the
 *   container to close the menu, so it is passed straight through.
 * @param compact - Use compact table menu rows.
 * @returns The menu item element.
 */
const renderRow = (
  item: DesktopDropdownMenuItem,
  compact = false
): React.ReactElement => {
  const colorClass =
    compact && item.destructive && !item.disabled
      ? "text-needs-semantic-error-600"
      : colorClassFor(item);
  return (
    <React.Fragment key={item.id}>
      {item.dividerBefore ? (
        <Box className="h-px w-full bg-outline-subtle" />
      ) : null}
      <Pressable
        accessibilityRole="menuitem"
        accessibilityLabel={item.label}
        accessibilityState={{ disabled: Boolean(item.disabled) }}
        onPress={item.onPress}
        disabled={item.disabled}
        testID={item.testID ?? TEST_ID_DESKTOP_DROPDOWN_MENU.itemFor(item.id)}
        className={`web:hover:bg-surface-raised ${compact ? "h-9 rounded-md" : `${ROW_CLASS} rounded-none`} justify-center px-3 ${
          item.disabled ? "opacity-40" : ""
        }`.trim()}
      >
        <Stack
          direction="horizontal"
          space="3"
          className="w-full items-center justify-between"
        >
          <Stack direction="horizontal" space="3" className="items-center">
            {item.icon ? (
              <Icon as={item.icon} size="sm" className={colorClass} />
            ) : null}
            <Text variant="body" className={colorClass}>
              {item.label}
            </Text>
          </Stack>
          {item.keyboardShortcut ? (
            <Text variant="caption" className="text-content-subtle">
              {item.keyboardShortcut}
            </Text>
          ) : null}
        </Stack>
      </Pressable>
    </React.Fragment>
  );
};

/** Public props for the DesktopDropdownMenu primitive. */
export interface DesktopDropdownMenuProps {
  /** Dense 36px rows and 8px panel radius/padding for table menus. */
  compact?: boolean;
  /** Whether the menu is open. */
  isOpen: boolean;
  /** Invoked when the menu requests close (item press, outside tap, back). */
  onClose: () => void;
  /** Toggles the menu — wired to the trigger press. */
  onToggle: () => void;
  /** Trigger content — the anchor visual (icon, pill, …). */
  triggerContent: React.ReactNode;
  /** testID applied to the measurable trigger pressable. */
  triggerTestID?: string;
  /** Accessibility label for the trigger pressable. */
  triggerAccessibilityLabel?: string;
  /** Closed-vocabulary classes for the trigger pressable. */
  triggerClassName?: string;
  /** Rows to render. */
  items: readonly DesktopDropdownMenuItem[];
  /**
   * Custom panel body rendered INSTEAD of `items`, for a menu whose rows are
   * not plain labels — group headers, an inline field, checkbox rows
   * (SE-8526, the selection bar's More ▾). The caller owns closing: nothing
   * in it is decorated with `onClose`.
   */
  content?: React.ReactNode;
  /** Panel max height in px before it scrolls. Defaults to 400. */
  maxHeight?: number;
  /**
   * Re-anchors the open panel when this changes — for a trigger that moves
   * because the layout around it changed (SE-8526).
   */
  remeasureKey?: unknown;
  /** Surface placement relative to the anchor. Defaults to "bottom-end". */
  placement?: DesktopDropdownMenuPlacement;
  /** Menu width in px. Defaults to 210. */
  width?: number;
  /** When true a small triangle points at the anchor. Defaults to false. */
  showPointer?: boolean;
}

/** Props for the presentational view (defaults + positioning resolved). */
export interface DesktopDropdownMenuViewProps {
  compact?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  triggerContent: React.ReactNode;
  triggerTestID?: string;
  triggerAccessibilityLabel?: string;
  triggerClassName?: string;
  /** Items whose `onPress` already chains the caller handler + close. */
  items: readonly DesktopDropdownMenuItem[];
  /** Custom panel body rendered instead of `items`. */
  content?: React.ReactNode;
  placement: DesktopDropdownMenuPlacement;
  showPointer: boolean;
  /** Measurement ref from `useDropdownPositioning` (container-owned). */
  triggerRef: React.RefObject<DropdownMeasurableNode | null>;
  /** Style-ready panel geometry (null until measured). */
  panelGeometry: DropdownPanelGeometry | null;
  /** SE-4953 flicker guard — overlay renders only when true. */
  shouldShow: boolean;
}

/**
 * DesktopDropdownMenuView — stateless menu surface on the shell.
 * @param props - View props.
 * @param props.compact - Use compact table menu spacing.
 * @param props.isOpen - Whether the menu is open.
 * @param props.onClose - Invoked when the menu requests close.
 * @param props.onToggle - Toggles the menu (trigger press).
 * @param props.triggerContent - Anchor visual content.
 * @param props.triggerTestID - testID for the trigger pressable.
 * @param props.triggerAccessibilityLabel - A11y label for the trigger.
 * @param props.triggerClassName - Classes for the trigger pressable.
 * @param props.items - Rows to render (onPress already decorated).
 * @param props.content - Custom panel body rendered instead of `items`.
 * @param props.placement - Surface placement relative to the anchor.
 * @param props.showPointer - Whether to render the pointer tail.
 * @param props.triggerRef - Measurement ref for the shell trigger.
 * @param props.panelGeometry - Measured panel geometry.
 * @param props.shouldShow - Overlay render guard.
 * @returns The anchored menu element.
 */
const DesktopDropdownMenuView = ({
  compact = false,
  isOpen,
  onClose,
  onToggle,
  triggerContent,
  triggerTestID,
  triggerAccessibilityLabel,
  triggerClassName,
  items,
  content,
  placement,
  showPointer,
  triggerRef,
  panelGeometry,
  shouldShow,
}: DesktopDropdownMenuViewProps): React.ReactElement => (
  <FixedPositionDropdownShell testID={TEST_ID_DESKTOP_DROPDOWN_MENU.ROOT}>
    <FixedPositionDropdownShellTrigger
      ref={triggerRef as React.RefObject<FixedPositionDropdownShellTriggerRef>}
      isOpen={isOpen}
      onPress={onToggle}
      testID={triggerTestID}
      accessibilityLabel={triggerAccessibilityLabel}
      className={triggerClassName}
    >
      {triggerContent}
    </FixedPositionDropdownShellTrigger>
    {shouldShow ? (
      <>
        <FixedPositionDropdownShellBackdrop
          onPress={onClose}
          accessibilityLabel="Close menu"
        />
        <FixedPositionDropdownShellPanel
          geometry={panelGeometry}
          onDismiss={onClose}
          className={
            compact
              ? "rounded-md border-outline-default !bg-surface-base p-2 shadow-hard-5"
              : "rounded-lg py-1 shadow-hard-5"
          }
        >
          {showPointer ? (
            <Icon
              testID={`${TEST_ID_DESKTOP_DROPDOWN_MENU.ROOT}.POINTER`}
              as={POINTER_ICON_BY_PLACEMENT[placement]}
              size="sm"
              className="absolute -top-2 right-4 text-surface-base"
            />
          ) : null}
          <ScrollView>
            <Box>{content ?? items.map(item => renderRow(item, compact))}</Box>
          </ScrollView>
        </FixedPositionDropdownShellPanel>
      </>
    ) : null}
  </FixedPositionDropdownShell>
);

DesktopDropdownMenuView.displayName = "DesktopDropdownMenuView";

export default memo(DesktopDropdownMenuView);
